/**
 * Nmbrs Sync Dashboard
 *
 * Shows sync health, last sync summary, and per-employee sync status table.
 * Story Nmbrs-2.2: Sync Status Dashboard
 *
 * Design system colors:
 * - Navy #2F5A7A: headers, primary
 * - Teal #54949E: secondary info
 * - Green #B7D945: healthy / success
 * - Red #D64545: errors only
 */

import { useSyncStatus } from '../hooks/useNmbrsSync';
import type { HealthStatus } from '../hooks/useNmbrsSync';
import { NmbrsSyncTrigger } from './NmbrsSyncTrigger';

const HEALTH_CONFIG: Record<HealthStatus, { label: string; bgColor: string; textColor: string; dotColor: string }> = {
  healthy: {
    label: 'Gezond',
    bgColor: 'bg-[#B7D945]/10',
    textColor: 'text-[#6B8C1A]',
    dotColor: 'bg-[#B7D945]',
  },
  warning: {
    label: 'Waarschuwing',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-800',
    dotColor: 'bg-amber-500',
  },
  error: {
    label: 'Fout',
    bgColor: 'bg-red-50',
    textColor: 'text-[#D64545]',
    dotColor: 'bg-[#D64545]',
  },
};

const SYNC_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  active: { label: 'Actief', color: 'text-[#6B8C1A]' },
  paused: { label: 'Gepauzeerd', color: 'text-amber-600' },
  error: { label: 'Fout', color: 'text-[#D64545]' },
};

const SYNC_TYPE_LABELS: Record<string, string> = {
  scheduled: 'Gepland',
  manual: 'Handmatig',
  single: 'Enkel',
};

const STATUS_LABELS: Record<string, string> = {
  running: 'Bezig...',
  completed: 'Voltooid',
  failed: 'Mislukt',
  partial: 'Gedeeltelijk',
};

function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Zojuist';
  if (minutes < 60) return `${minutes} min geleden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} uur geleden`;
  const days = Math.floor(hours / 24);
  return `${days} dag${days !== 1 ? 'en' : ''} geleden`;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function NmbrsSyncDashboard() {
  const { data, isLoading, error } = useSyncStatus();

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 py-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2F5A7A]" />
        <span className="text-sm text-gray-500">Sync status laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 text-[#D64545] text-sm">
        Kan sync status niet laden: {error instanceof Error ? error.message : 'Onbekende fout'}
      </div>
    );
  }

  const status = data?.data;
  if (!status) return null;

  const healthConfig = HEALTH_CONFIG[status.health];
  const lastSync = status.lastSync;

  return (
    <div className="space-y-6">
      {/* Health Badge + Last Sync Summary + Sync Trigger */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#2F5A7A]">Sync Status</h3>
          <div className="flex items-center gap-3">
            <NmbrsSyncTrigger />
            <span
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${healthConfig.bgColor} ${healthConfig.textColor}`}
            >
              <span className={`h-2 w-2 rounded-full ${healthConfig.dotColor}`} />
              {healthConfig.label}
            </span>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">{status.healthReason}</p>

        {lastSync && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-[#E2E8F0]">
            <div>
              <p className="text-xs text-gray-500 mb-1">Laatste sync</p>
              <p className="text-sm font-medium text-[#1E293B]">
                {formatRelativeTime(lastSync.completedAt ?? lastSync.startedAt)}
              </p>
              <p className="text-xs text-[#54949E]">
                {formatDate(lastSync.completedAt ?? lastSync.startedAt)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Type</p>
              <p className="text-sm font-medium text-[#1E293B]">
                {SYNC_TYPE_LABELS[lastSync.syncType] ?? lastSync.syncType}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <p className="text-sm font-medium text-[#1E293B]">
                {STATUS_LABELS[lastSync.status] ?? lastSync.status}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Resultaat</p>
              <p className="text-sm font-medium text-[#1E293B]">
                {lastSync.syncedCount}/{lastSync.totalEmployees} gesynchroniseerd
                {lastSync.errorCount > 0 && (
                  <span className="text-[#D64545] ml-1">
                    ({lastSync.errorCount} fout{lastSync.errorCount !== 1 ? 'en' : ''})
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {!lastSync && (
          <p className="text-sm text-gray-500 italic pt-4 border-t border-[#E2E8F0]">
            Er is nog geen synchronisatie uitgevoerd.
          </p>
        )}
      </div>

      {/* Employee Sync Status Table */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm overflow-hidden">
        <div className="p-4 border-b border-[#E2E8F0]">
          <h4 className="text-sm font-semibold text-[#2F5A7A]">
            Medewerker Sync Status ({status.employeeStatuses.length})
          </h4>
        </div>

        {status.employeeStatuses.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-500">
              Geen gekoppelde medewerkers. Koppel medewerkers in het tabblad "Koppelingen".
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E2E8F0]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Naam
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Nmbrs ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Laatst gesynchroniseerd
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {status.employeeStatuses.map((emp) => {
                  const statusConfig = SYNC_STATUS_LABELS[emp.syncStatus] ?? {
                    label: emp.syncStatus,
                    color: 'text-gray-600',
                  };
                  return (
                    <tr key={emp.employeeId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-[#1E293B]">
                        {emp.employeeName}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#54949E] font-mono">
                        {emp.nmbrsEmployeeId}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {emp.lastSyncedAt ? formatRelativeTime(emp.lastSyncedAt) : 'Nog niet gesynchroniseerd'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
